import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import TreatmentPhaseTimeline from './components/TreatmentPhaseTimeline';
import ImagingIntegrationPanel from './components/ImagingIntegrationPanel';
import TreatmentTemplateLibrary from './components/TreatmentTemplateLibrary';
import CollaborationPanel from './components/CollaborationPanel';
import RecallScheduling from './components/RecallScheduling';
import ProgressTracking from './components/ProgressTracking';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TreatmentPlanning = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('planning'); // planning, imaging, templates, collaboration
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showImagingPanel, setShowImagingPanel] = useState(true);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [draggedPhase, setDraggedPhase] = useState(null);

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1978",
    age: "48 years"
  };

  const patientAlerts = [
    {
      id: 1,
      severity: "warning",
      title: "Treatment Plan Review Pending",
      message: "Comprehensive treatment plan requires multi-disciplinary approval before proceeding",
      dismissible: true
    },
    {
      id: 2,
      severity: "info",
      title: "Insurance Pre-Authorization",
      message: "Crown procedure pre-authorization approved - valid until 03/15/2026",
      dismissible: true
    }
  ];

  const [treatmentPlans, setTreatmentPlans] = useState([
    {
      id: 1,
      planName: "Comprehensive Restorative Care",
      patient: "Sarah Johnson",
      mrn: "MRN-2024-789456",
      primaryDentist: "Dr. Michael Chen, DDS",
      status: "in-progress",
      createdDate: "2026-01-10",
      lastUpdated: "2026-01-18",
      estimatedDuration: "6 months",
      totalCost: "8,450.00",
      insuranceCoverage: "4,200.00",
      patientResponsibility: "4,250.00",
      completionPercentage: 35,
      phases: [
        {
          id: "phase-1",
          phaseName: "Initial Assessment & Diagnostics",
          status: "completed",
          startDate: "2026-01-10",
          endDate: "2026-01-15",
          procedures: [
            { code: "D0150", name: "Comprehensive oral evaluation", status: "completed", cost: "125.00" },
            { code: "D0274", name: "Bitewing radiographs - four films", status: "completed", cost: "85.00" },
            { code: "D0330", name: "Panoramic radiograph", status: "completed", cost: "145.00" }
          ],
          provider: "Dr. Michael Chen",
          notes: "Complete diagnostic workup completed. Moderate caries detected on #3, #14. Periodontal health good."
        },
        {
          id: "phase-2",
          phaseName: "Restorative Treatment",
          status: "in-progress",
          startDate: "2026-01-20",
          endDate: "2026-03-15",
          procedures: [
            { code: "D2391", name: "Resin composite - one surface, posterior", tooth: "#3", status: "completed", cost: "185.00" },
            { code: "D2740", name: "Crown - porcelain fused to metal", tooth: "#14", status: "scheduled", cost: "1,250.00", scheduledDate: "2026-02-05" },
            { code: "D2950", name: "Core buildup", tooth: "#14", status: "pending", cost: "295.00" }
          ],
          provider: "Dr. Michael Chen",
          notes: "Composite filling completed on #3. Crown prep scheduled for #14."
        },
        {
          id: "phase-3",
          phaseName: "Implant Placement",
          status: "pending",
          startDate: "2026-03-20",
          endDate: "2026-06-15",
          procedures: [
            { code: "D6010", name: "Surgical placement of implant", tooth: "#30 site", status: "pending", cost: "2,500.00" },
            { code: "D6065", name: "Implant supported crown", tooth: "#30", status: "pending", cost: "1,850.00" },
            { code: "D6104", name: "Bone graft", status: "pending", cost: "850.00" }
          ],
          provider: "Dr. Emily Rodriguez",
          specialty: "Oral Surgery",
          notes: "Referral to oral surgeon for implant placement. Adequate bone density confirmed via CBCT."
        },
        {
          id: "phase-4",
          phaseName: "Maintenance & Recall",
          status: "pending",
          startDate: "2026-07-01",
          endDate: "Ongoing",
          procedures: [
            { code: "D1110", name: "Prophylaxis - adult", status: "pending", cost: "125.00", recallInterval: "6 months" },
            { code: "D0274", name: "Bitewing radiographs", status: "pending", cost: "85.00", recallInterval: "12 months" }
          ],
          provider: "Sarah Williams, RDH",
          notes: "Regular maintenance schedule established. Patient enrolled in 6-month recall program."
        }
      ],
      linkedImaging: [
        { id: 1, type: "Bitewing", date: "2026-01-15", toothNumber: "#14-15", status: "reviewed" },
        { id: 7, type: "Panoramic", date: "2026-01-05", toothNumber: "Full Mouth", status: "reviewed" },
        { id: 15, type: "CBCT", date: "2026-01-08", toothNumber: "#30 site", status: "reviewed" }
      ],
      collaborators: [
        { name: "Dr. Michael Chen", role: "Primary Dentist", status: "approved", date: "2026-01-10" },
        { name: "Dr. Emily Rodriguez", role: "Oral Surgeon", status: "pending", date: null },
        { name: "Sarah Williams, RDH", role: "Dental Hygienist", status: "approved", date: "2026-01-12" }
      ],
      recallSchedule: [
        { type: "6-Month Cleaning", dueDate: "2026-07-10", status: "scheduled", notificationSent: true },
        { type: "Annual X-rays", dueDate: "2027-01-10", status: "pending", notificationSent: false },
        { type: "Implant Follow-up", dueDate: "2026-09-15", status: "pending", notificationSent: false }
      ],
      patientConsent: {
        obtained: true,
        date: "2026-01-10",
        signedBy: "Sarah Johnson",
        documents: ["Treatment Plan Consent", "Financial Agreement", "HIPAA Authorization"]
      }
    }
  ]);

  const treatmentTemplates = [
    {
      id: 1,
      name: "Comprehensive Restorative Care",
      specialty: "General Dentistry",
      description: "Multi-phase treatment for extensive restorative needs including fillings, crowns, and implants",
      estimatedDuration: "4-6 months",
      averageCost: "$6,000 - $10,000",
      phases: 4,
      procedures: 12
    },
    {
      id: 2,
      name: "Orthodontic Treatment Plan",
      specialty: "Orthodontics",
      description: "Comprehensive orthodontic care with braces or clear aligners",
      estimatedDuration: "18-24 months",
      averageCost: "$4,500 - $7,500",
      phases: 3,
      procedures: 8
    },
    {
      id: 3,
      name: "Periodontal Therapy Protocol",
      specialty: "Periodontics",
      description: "Scaling, root planing, and maintenance for periodontal disease management",
      estimatedDuration: "3-6 months",
      averageCost: "$1,500 - $3,000",
      phases: 3,
      procedures: 6
    },
    {
      id: 4,
      name: "Full Mouth Reconstruction",
      specialty: "Prosthodontics",
      description: "Complete rehabilitation with crowns, bridges, and implants",
      estimatedDuration: "12-18 months",
      averageCost: "$25,000 - $45,000",
      phases: 5,
      procedures: 20
    },
    {
      id: 5,
      name: "Cosmetic Enhancement Package",
      specialty: "Cosmetic Dentistry",
      description: "Veneers, whitening, and aesthetic improvements",
      estimatedDuration: "2-3 months",
      averageCost: "$3,000 - $8,000",
      phases: 2,
      procedures: 5
    },
    {
      id: 6,
      name: "Pediatric Preventive Care",
      specialty: "Pediatric Dentistry",
      description: "Comprehensive preventive care for children including sealants and fluoride",
      estimatedDuration: "Ongoing",
      averageCost: "$200 - $500/year",
      phases: 2,
      procedures: 4
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'bg-success/10 text-success border-success';
      case 'in-progress': case'scheduled':
        return 'bg-primary/10 text-primary border-primary';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      case 'cancelled': case'rejected':
        return 'bg-error/10 text-error border-error';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const handleDragStart = (phase) => {
    setDraggedPhase(phase);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleDrop = (targetPhase) => {
    if (!draggedPhase || draggedPhase?.id === targetPhase?.id) return;
    
    // Reorder phases logic would go here
    console.log('Reordering phases:', draggedPhase, targetPhase);
    setDraggedPhase(null);
  };

  const handleApplyTemplate = (template) => {
    console.log('Applying template:', template);
    setShowTemplateLibrary(false);
  };

  const selectedPlanData = selectedPlan || treatmentPlans?.[0];

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <PatientContextBar 
          patient={currentPatient} 
          alerts={patientAlerts}
          onAlertClick={(alert) => console.log('Alert clicked:', alert)}
        />
        
        <div className="flex-1 overflow-auto">
          <div className="max-w-screen-2xl mx-auto p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                  <Icon name="ClipboardList" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground">Treatment Planning</h1>
                  <p className="text-sm text-muted-foreground">Comprehensive care coordination with imaging integration</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="FileText" onClick={() => {}}>
                  Export Plan
                </Button>
                <Button variant="outline" size="sm" iconName="BookOpen" onClick={() => setShowTemplateLibrary(!showTemplateLibrary)}>
                  Templates
                </Button>
                <Button variant="default" size="sm" iconName="Plus">
                  New Plan
                </Button>
              </div>
            </div>

            {/* View Tabs */}
            <div className="flex items-center gap-2 border-b border-border overflow-x-auto">
              {['planning', 'imaging', 'collaboration', 'progress']?.map((view) => (
                <button
                  key={view}
                  onClick={() => setActiveView(view)}
                  className={`px-4 py-2 text-sm font-medium transition-smooth border-b-2 whitespace-nowrap ${
                    activeView === view
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {view?.charAt(0)?.toUpperCase() + view?.slice(1)}
                </button>
              ))}
            </div>

            {/* Template Library Modal */}
            {showTemplateLibrary && (
              <TreatmentTemplateLibrary
                templates={treatmentTemplates}
                onApplyTemplate={handleApplyTemplate}
                onClose={() => setShowTemplateLibrary(false)}
              />
            )}

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Primary Planning Workspace */}
              <div className="lg:col-span-2 space-y-6">
                {activeView === 'planning' && (
                  <>
                    {/* Treatment Plan Overview */}
                    <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h2 className="text-xl font-semibold text-foreground mb-2">{selectedPlanData?.planName}</h2>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Icon name="User" size={14} />
                              {selectedPlanData?.primaryDentist}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="Calendar" size={14} />
                              Created {selectedPlanData?.createdDate}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="Clock" size={14} />
                              {selectedPlanData?.estimatedDuration}
                            </span>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border ${getStatusColor(selectedPlanData?.status)}`}>
                          {selectedPlanData?.status?.toUpperCase()}
                        </span>
                      </div>

                      {/* Cost Summary */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Total Cost</p>
                          <p className="text-lg font-semibold text-foreground">${selectedPlanData?.totalCost}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Insurance Coverage</p>
                          <p className="text-lg font-semibold text-success">${selectedPlanData?.insuranceCoverage}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Patient Responsibility</p>
                          <p className="text-lg font-semibold text-warning">${selectedPlanData?.patientResponsibility}</p>
                        </div>
                      </div>
                    </div>

                    {/* Treatment Phase Timeline */}
                    <TreatmentPhaseTimeline
                      phases={selectedPlanData?.phases}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      getStatusColor={getStatusColor}
                    />
                  </>
                )}

                {activeView === 'imaging' && (
                  <ImagingIntegrationPanel
                    linkedImaging={selectedPlanData?.linkedImaging}
                    planId={selectedPlanData?.id}
                  />
                )}

                {activeView === 'collaboration' && (
                  <CollaborationPanel
                    collaborators={selectedPlanData?.collaborators}
                    planId={selectedPlanData?.id}
                    patientConsent={selectedPlanData?.patientConsent}
                  />
                )}

                {activeView === 'progress' && (
                  <ProgressTracking
                    plan={selectedPlanData}
                    getStatusColor={getStatusColor}
                  />
                )}
              </div>

              {/* Right Sidebar - Imaging & Recall */}
              <div className="space-y-6">
                {/* Linked Imaging Quick View */}
                {showImagingPanel && (
                  <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Icon name="Image" size={16} className="text-primary" />
                        Linked Imaging
                      </h3>
                      <button onClick={() => setShowImagingPanel(false)}>
                        <Icon name="X" size={16} className="text-muted-foreground" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {selectedPlanData?.linkedImaging?.map((image) => (
                        <div key={image?.id} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg hover:bg-muted transition-smooth cursor-pointer">
                          <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                            <Icon name="Image" size={20} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">{image?.type}</p>
                            <p className="text-xs text-muted-foreground">{image?.toothNumber} • {image?.date}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${getStatusColor(image?.status)}`}>
                            {image?.status}
                          </span>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-3" iconName="Plus">
                      Link More Images
                    </Button>
                  </div>
                )}

                {/* Recall Scheduling */}
                <RecallScheduling
                  recallSchedule={selectedPlanData?.recallSchedule}
                  planId={selectedPlanData?.id}
                  getStatusColor={getStatusColor}
                />

                {/* Patient Consent Status */}
                <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="FileCheck" size={16} className="text-primary" />
                    Patient Consent
                  </h3>
                  {selectedPlanData?.patientConsent?.obtained ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-success">
                        <Icon name="CheckCircle" size={16} />
                        <span>Consent Obtained</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Signed by {selectedPlanData?.patientConsent?.signedBy} on {selectedPlanData?.patientConsent?.date}
                      </p>
                      <div className="mt-3 space-y-1">
                        {selectedPlanData?.patientConsent?.documents?.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Icon name="FileText" size={12} />
                            <span>{doc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-warning">
                      <Icon name="AlertCircle" size={16} />
                      <span>Consent Pending</span>
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="w-full mt-3" iconName="Edit">
                    Update Consent
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <QuickActions />
        <SessionManager 
          onSessionExpired={() => console.log('Session expired')}
          onSessionExtended={() => console.log('Session extended')}
        />
      </div>
    </div>
  );
};

export default TreatmentPlanning;